import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Send } from 'react-feather';
import { useAppDispatch, useAppSelector } from '../../../../../redux/store';
import { CheckCircle, CircleDashed, Plus } from 'phosphor-react';
import { AddPaymentRequestModal } from '../../../suppliers/PaymentRequests/AddPaymentRequestModal/AddPaymentRequestModal';
import { getPaymentRequests } from '../../../../../redux/services/paymentRequestsService';
import PaymentRequestsTable from '../../../suppliers/PaymentRequests/PaymentRequestsTable/PaymentRequestsTable';
import { Buyer, canUserAccessBuyerBranch, DillPaymentRequestResponse } from '@dill/dill-shared';
import moment from 'moment';
import { COLORS } from '../../../../../utils/colors';
import AppDatePicker from '../../../creditApplication/CreditApplicationComponents/AppDatePicker';
import { DillButton, DillCheckbox, DillDropDownFilter } from '../../../../general/Dill';

export const SupplierCustomerPaymentRequests = ({
  buyerId,
  buyer,
}: {
  buyerId: string;
  buyer?: Buyer | null;
}) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const paymentRequestIdToView =
    new URLSearchParams(location.search).get('paymentRequestId') || undefined;
  const [searchText, setSearchText] = useState('');
  const [isAddPaymentRequestModalOpen, setIsAddPaymentRequestModalOpen] = useState(false);
  const { paymentRequests } = useAppSelector((state) => state.paymentRequests);
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const [searchedPaymentRequests, setSearchedPaymentRequests] = useState<
    DillPaymentRequestResponse[]
  >([]);
  const [selectedFilters, setSelectedFilters] = useState<{ [key: string]: string[] }>({});
  const [dateRange, setDateRange] = useState<{ from: Date | null; to: Date | null }>({
    from: null,
    to: null,
  });

  useEffect(() => {
    if (user) {
      dispatch(getPaymentRequests({ supplierId: currentSupplier?.id || '', buyerId }));
    }
    return () => {};
  }, [user?.id, buyerId]);

  useEffect(() => {
    if (paymentRequests || searchText) {
      const searched = [...paymentRequests]
        .filter((paymentRequestDetails) => {
          if (
            (paymentRequestDetails?.buyer?.name &&
              paymentRequestDetails?.buyer?.name
                .toString()
                .toLowerCase()
                .includes(searchText.toLowerCase())) ||
            (currentSupplier?.integrationType !== 'RUTTER_QBD' &&
              paymentRequestDetails?.buyer?.quickbooksCustomerId &&
              paymentRequestDetails.buyer.name &&
              `${paymentRequestDetails.buyer?.name} - ${paymentRequestDetails.buyer?.quickbooksCustomerId}`
                .toString()
                .toLowerCase()
                .includes(searchText.toLowerCase())) ||
            (paymentRequestDetails.emails &&
              paymentRequestDetails?.emails.some((emailItem) =>
                emailItem.toString().toLowerCase().includes(searchText.toLowerCase())
              )) ||
            (paymentRequestDetails?.notes &&
              paymentRequestDetails?.notes
                .toString()
                .toLowerCase()
                .includes(searchText.toLowerCase())) ||
            (paymentRequestDetails?.status &&
              paymentRequestDetails?.status
                .toString()
                .replaceAll('_', ' ')
                .replaceAll('UNPAID', 'SENT')
                .toLowerCase()
                .includes(searchText.toLowerCase())) ||
            (paymentRequestDetails?.balance &&
              Number(paymentRequestDetails?.balance)
                .toFixed(2)
                .toString()
                .includes(searchText.toLowerCase())) ||
            (paymentRequestDetails?.totalAmount &&
              Number(paymentRequestDetails?.totalAmount)
                .toFixed(2)
                .toString()
                .includes(searchText.toLowerCase())) ||
            (paymentRequestDetails.createdAt &&
              moment(paymentRequestDetails.createdAt)
                .format('MM/DD/YYYY')
                .toLowerCase()
                .includes(searchText.toLowerCase()))
          ) {
            return true;
          }
          return false;
        })
        .sort((A, B) => {
          if (!A.createdAt || !B.createdAt) {
            return -1;
          }
          return new Date(B.createdAt).getTime() - new Date(A.createdAt).getTime();
        });
      setSearchedPaymentRequests(searched);
    }
  }, [paymentRequests, searchText]);

  const paymentStatus = useMemo(() => {
    const status = paymentRequests.map((paymentRequest) => paymentRequest.status);
    return Array.from(new Set(status));
  }, [paymentRequests]);

  const handleSelectFilter = (selected: { [key: string]: string[] }) => {
    setSelectedFilters(selected);
    if (dateRange?.from && dateRange?.to && selected?.dateRequested?.length === 0) {
      setDateRange({ from: null, to: null });
    }

    const hasSelections = Object.values(selected).some((arr) => (arr?.length ?? 0) > 0);
    if (!hasSelections && !dateRange.from && !dateRange.to) {
      setSearchedPaymentRequests(paymentRequests);
      return;
    }

    const filtered = paymentRequests.filter((application) => {
      const matchesFilters = Object.entries(selected).every(([key, values]) => {
        if (!values || values.length === 0) return true;

        if (key === 'dateRequested' && values.length === 0) {
          return values;
        } else if (key === 'dateRequested') {
          const matchesDate =
            !dateRange.from ||
            !dateRange.to ||
            (moment(application.createdAt).isSameOrAfter(dateRange.from, 'day') &&
              moment(application.createdAt).isSameOrBefore(dateRange.to, 'day'));
          return matchesDate;
        }

        const appValueRaw = (application as Record<string, unknown>)[key];
        if (appValueRaw == null) return false;

        const appValue = String(appValueRaw).toLowerCase();
        return values.some((v) => v.toLowerCase() === appValue);
      });

      return matchesFilters;
    });

    setSearchedPaymentRequests(filtered);
  };

  useEffect(() => {
    if (!(dateRange.from && dateRange.to)) return;
    handleSelectFilter({
      ...selectedFilters,
      dateRequested: [
        `${moment(dateRange?.from).format('MM/DD/YY')} - ${moment(dateRange?.to).format(
          'MM/DD/YY'
        )}`,
      ],
    });
    return () => {};
  }, [dateRange]);

  const toolbarLeft = (
    <div className="w-[280px]">
      <DillDropDownFilter
        placeholder="Filters"
        closeMenu={false}
        isMulti
        showSelectedItems
        multiValue={selectedFilters}
        inputHeight={34}
        items={[
          {
            id: 'dateRequested',
            label: 'Filter by date requested',
            value: [
              {
                label: `${dateRange?.from ? moment(dateRange?.from).format('MM/DD/YY') : ''} - ${
                  dateRange?.to ? moment(dateRange?.to).format('MM/DD/YY') : ''
                }`,
                value: `${dateRange?.from ? moment(dateRange?.from).format('MM/DD/YY') : ''} - ${
                  dateRange?.to ? moment(dateRange?.to).format('MM/DD/YY') : ''
                }`,
                isDisabled: true,
                labelInElement: (
                  <div>
                    <div className="w-fit flex flex-row gap-2 text-black">
                      <AppDatePicker
                        selectedDate={dateRange?.from}
                        maximumDate={dateRange?.to || new Date()}
                        label={'From'}
                        onDateSelected={(date) => {
                          if (date) {
                            setDateRange((prev) => ({
                              ...prev,
                              from: date,
                            }));
                          }
                        }}
                      />
                      <AppDatePicker
                        selectedDate={dateRange?.to}
                        maximumDate={new Date()}
                        minumumDate={dateRange?.from}
                        label={'To'}
                        onDateSelected={(date) => {
                          if (date) {
                            setDateRange((prev) => ({
                              ...prev,
                              to: date,
                            }));
                          }
                        }}
                      />
                    </div>
                    <div>
                      <button
                        className="PRIMARY_500-CLR text-xs mt-1"
                        onClick={() => {
                          setDateRange({ to: null, from: null });
                          handleSelectFilter({
                            ...selectedFilters,
                            dateRequested: [],
                          });
                        }}>
                        Clear
                      </button>
                    </div>
                  </div>
                ),
              },
            ],
          },
          {
            id: 'status',
            label: 'Filter by status',
            value: [
              ...paymentStatus.map((item) => ({
                label:
                  item === 'PAID'
                    ? 'Paid'
                    : item === 'PAYMENT_INITIATED'
                    ? 'Payment Initiated'
                    : item === 'PATIALLY_PAID'
                    ? 'Partially Paid'
                    : 'Sent',
                value: item,
                labelInElement: (
                  <div className="w-fit flex flex-col">
                    {item === 'PAID' && (
                      <div className="flex flex-row gap-1 items-center">
                        <DillCheckbox
                          isChecked={selectedFilters?.status?.includes('PAID') ?? false}
                        />
                        <div className="flex SUCCESS_100-BG text-xs SUCCESS_500-CLR font-semibold px-2 py-1 rounded-full items-center justify-center">
                          <CheckCircle size={14} color={COLORS.SUCCESS_500} />
                          <div className="ml-2 text-xs">Paid</div>
                        </div>
                      </div>
                    )}
                    {item === 'UNPAID' && (
                      <div className="flex flex-row gap-1 items-center">
                        <DillCheckbox
                          isChecked={selectedFilters?.status?.includes('UNPAID') ?? false}
                        />
                        <div className="flex PRIMARY_100-BG text-xs PRIMARY_500-CLR font-semibold px-2 py-1 rounded-full items-center justify-center">
                          <Send size={14} color={COLORS.PRIMARY_500} />
                          <div className="ml-2 text-xs">Sent</div>
                        </div>
                      </div>
                    )}
                    {item === 'PAYMENT_INITIATED' && (
                      <div className="flex flex-row gap-1 items-center">
                        <DillCheckbox
                          isChecked={
                            selectedFilters?.status?.includes('PAYMENT_INITIATED') ?? false
                          }
                        />
                        <div className="flex INFO_100-BG text-xs INFO_500-CLR font-semibold px-2 py-1 rounded-full items-center justify-center text-nowrap">
                          <CircleDashed size={20} color={COLORS.INFO_500} />
                          <div className="ml-2 text-xs">Payment Initiated</div>
                        </div>
                      </div>
                    )}
                    {item === 'PATIALLY_PAID' && (
                      <div className="flex flex-row gap-1 items-center">
                        <DillCheckbox
                          isChecked={selectedFilters?.status?.includes('PATIALLY_PAID') ?? false}
                        />
                        <div className="flex SUCCESS_100-BG text-xs SUCCESS_500-CLR font-semibold px-2 py-1 rounded-full items-center justify-center">
                          <CheckCircle size={14} color={COLORS.SUCCESS_500} />
                          <div className="ml-2 text-xs">Partially Paid</div>
                        </div>
                      </div>
                    )}
                  </div>
                ),
              })),
            ],
          },
        ]}
        itemWidth={180}
        showMinimalDesign
        onMultiSelectChange={(data) => {
          if (data) {
            const transformed = Object.fromEntries(
              Object.entries(data).map(([key, arr]) => [
                key,
                (arr as { value: string }[]).map((item) => item.value),
              ])
            );
            handleSelectFilter(transformed);
          }
        }}
      />
    </div>
  );

  // A branch-scoped user cannot create a payment request for a customer outside their branches —
  // the backend rejects it, so don't offer the action.
  const canRequestPayment = canUserAccessBuyerBranch({
    user,
    supplierId: currentSupplier?.id,
    buyerBranch: buyer?.branch,
  });

  const toolbarRight = (
    <div className="flex flex-row items-center gap-2">
      {canRequestPayment && (
        <DillButton
          label="Add Payment Request"
          onClick={() => {
            setIsAddPaymentRequestModalOpen(true);
          }}
          icon={<Plus size={16} />}
        />
      )}
    </div>
  );

  return (
    <div className="w-full h-full ">
      <AddPaymentRequestModal
        open={isAddPaymentRequestModalOpen}
        handleClose={() => {
          setIsAddPaymentRequestModalOpen(false);
        }}
        onPaymentRequestAdded={() => {
          setIsAddPaymentRequestModalOpen(false);
          dispatch(getPaymentRequests({ supplierId: currentSupplier?.id || '', buyerId }));
        }}
      />
      <div className="flex flex-col h-full relative">
        <div style={{ height: '100%' }}>
          <PaymentRequestsTable
            paymentRequests={searchedPaymentRequests}
            paymentRequestIdToView={paymentRequestIdToView}
            toolbarLeft={toolbarLeft}
            toolbarRight={toolbarRight}
            hideCustomerColumn
          />
        </div>
      </div>
    </div>
  );
};
